import PropTypes from "prop-types";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Kbd } from "@/components/ui/kbd";
import { ToolbarButton } from "@/components/ui/toolbar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

function ToolbarToggle({
  label,
  tooltip,
  icon: Icon,
  active,
  disabled,
  shortcut,
  shortcutKeys,
  onSelect,
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          // ToolbarButton owns the disabled state so the control stays in the
          // roving order and remains focusable when disabled; Base UI reports
          // that through data-disabled rather than the native attribute, which
          // also keeps the tooltip reachable.
          <ToolbarButton
            disabled={disabled}
            render={
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={label}
                aria-pressed={active}
                aria-keyshortcuts={shortcutKeys || undefined}
                className={cn(
                  "tw:aria-pressed:bg-muted tw:aria-pressed:text-foreground tw:dark:aria-pressed:bg-[color-mix(in_oklch,var(--color-highlight)_15%,transparent)] tw:dark:aria-pressed:text-[var(--color-highlight)]",
                  // The Button dims via the native :disabled pseudo-class, which
                  // no longer applies; key the disabled look off data-disabled
                  // instead. No pointer-events-none, so the tooltip still opens.
                  "tw:data-disabled:cursor-not-allowed tw:data-disabled:text-[var(--color-text-muted)] tw:data-disabled:opacity-50"
                )}
                // Pressing the control must not collapse the editor selection,
                // or the command would apply to nothing.
                onMouseDown={event => event.preventDefault()}
                onClick={disabled ? undefined : onSelect}
              />
            }
          />
        }
      >
        <Icon />
      </TooltipTrigger>
      <TooltipContent>
        {tooltip ?? label}
        {shortcut && <Kbd>{shortcut}</Kbd>}
      </TooltipContent>
    </Tooltip>
  );
}

ToolbarToggle.propTypes = {
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  icon: PropTypes.elementType.isRequired,
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  shortcut: PropTypes.string,
  shortcutKeys: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

export default ToolbarToggle;
